import type { PatternContent } from './types'

export const STRUCTURAL_AND_EARLY_BEHAVIOURAL: PatternContent[] = [
  {
    slug: 'decorator',
    teach: [
      {
        label: 'The problem',
        text: 'You want logging, caching or retries around a class, but subclassing for every mix gives you a pile of classes. Behaviour should stack, not multiply.',
      },
      {
        label: 'The shape',
        text: 'A wrapper implements the same interface as the thing it holds, does its extra bit, then hands the call on. Wrappers can wrap wrappers, in any order.',
      },
      {
        label: 'A trap',
        text: 'A wrapper that only guards or delays access to the real object is a Proxy. A Decorator adds something to what the caller gets back or what happens.',
      },
    ],
    whenToUse:
      'You need to add behaviour around an existing object at runtime, in combinations, without touching its class or the callers.',
    whenNotTo:
      'The extra behaviour is needed once, or order of wrapping would matter in subtle ways. A plain method or a subclass is easier to read.',
    quizzes: [
      {
        format: 'judge',
        prompt: 'This is a Decorator.',
        code: `class LazyReport implements Report
{
    private ?Report $real = null;

    public function render(): string
    {
        $this->real ??= new HeavyReport();
        return $this->real->render();
    }
}`,
        choices: [{ label: 'True' }, { label: 'False', correct: true }],
        explanation: 'It only delays building the real report and adds nothing to it: that is a Proxy.',
      },
      {
        format: 'quick_pick',
        prompt: 'What lets you stack three decorators in any order?',
        choices: [
          { label: 'They share the wrapped interface', correct: true },
          { label: 'They extend one common base class' },
          { label: 'A registry sorts them by priority' },
          { label: 'Each one is a singleton' },
        ],
        explanation: 'Each wrapper looks like the real thing, so it can wrap another wrapper just as well.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `class LoggedMailer implements Mailer
{
    public function __construct(
        private Mailer $inner,
        private Logger $log,
    ) {}

    public function send(Message $m): void
    {
        $this->log->info('sending');
        $this->inner->send($m);
    }
}`,
        choices: [
          { label: 'Decorator', correct: true },
          { label: 'Proxy' },
          { label: 'None of these' },
          { label: 'Misused' },
        ],
        explanation: 'Same Mailer interface, extra logging, then the call goes on to the inner mailer.',
      },
    ],
  },
  {
    slug: 'facade',
    teach: [
      {
        label: 'The problem',
        text: 'Placing an order means calling stock, payment and mail in the right order. Every caller repeating that sequence will get it wrong somewhere.',
      },
      {
        label: 'The shape',
        text: 'One class offers a few plain methods like place(). It knows the subsystem and its order of calls; callers know only the front door.',
      },
      {
        label: 'A trap',
        text: 'A class that forwards each call one-to-one hides nothing. And Laravel Facades are static access to the container, not this pattern.',
      },
    ],
    whenToUse:
      'A subsystem has many parts and most callers want the same few outcomes. A front door keeps the sequence in one place.',
    whenNotTo:
      'The subsystem is already simple, or callers need its full power. A facade that mirrors every method is just extra indirection.',
    quizzes: [
      {
        format: 'judge',
        prompt: 'This is a Facade.',
        code: `class Checkout
{
    public function place(Cart $c): void
    {
        $this->stock->reserve($c);
        $this->pay->charge($c->total());
        $this->mail->receipt($c);
    }
}`,
        choices: [{ label: 'True', correct: true }, { label: 'False' }],
        explanation: 'One simple call hides three subsystems and the order they must run in.',
      },
      {
        format: 'quick_pick',
        prompt: 'What does a Facade promise its callers?',
        choices: [
          { label: 'Same interface as the subsystem' },
          { label: 'A few simple calls, detail hidden', correct: true },
          { label: 'Only one instance ever exists' },
          { label: 'The subsystem loads lazily' },
        ],
        explanation: 'It narrows a wide subsystem into the handful of calls people actually need.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `class BillingFacade
{
    public function __construct(
        private Stripe $s,
    ) {}
    public function charge($a)
    { return $this->s->charge($a); }
    public function refund($a)
    { return $this->s->refund($a); }
}`,
        choices: [
          { label: 'Facade' },
          { label: 'Adapter' },
          { label: 'None of these' },
          { label: 'Misused', correct: true },
        ],
        explanation: 'It forwards every call one-to-one, so it simplifies and hides nothing.',
      },
    ],
  },
  {
    slug: 'flyweight',
    teach: [
      {
        label: 'The problem',
        text: 'A page holds a hundred thousand characters, each with its own glyph shape object. Almost all of that data is identical, and memory pays for every copy.',
      },
      {
        label: 'The shape',
        text: 'Split what never changes (shape, texture) from what does (position). Keep one shared object per distinct value, handed out by a small factory.',
      },
      {
        label: 'A trap',
        text: 'Shared objects must be immutable. If one caller can change a flyweight, every other holder sees it change too.',
      },
    ],
    whenToUse:
      'You create huge numbers of objects that mostly repeat the same heavy data, and memory is a measured problem.',
    whenNotTo:
      'You have a few hundred objects, or the data differs for each one. Sharing adds a factory and a rule about immutability for nothing.',
    quizzes: [
      {
        format: 'judge',
        prompt: 'This is a Flyweight.',
        code: `foreach ($rows as $row) {
    $out[] = new Cell(
        $row,
        new Style('arial', 12),
    );
}
// ten thousand rows
// ten thousand Styles`,
        choices: [{ label: 'True' }, { label: 'False', correct: true }],
        explanation: 'Every cell builds its own identical Style; nothing is shared, which is the opposite.',
      },
      {
        format: 'quick_pick',
        prompt: 'Which data lives inside the shared flyweight object?',
        choices: [
          { label: "Each caller's position" },
          { label: 'Unchanging data many uses share', correct: true },
          { label: 'The database connection' },
          { label: 'The undo history' },
        ],
        explanation: 'Only the part that is the same everywhere is shared; context stays outside.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `var glyphs = map[rune]*Glyph{}

func GlyphFor(r rune) *Glyph {
	if g, ok := glyphs[r]; ok {
		return g
	}
	g := &Glyph{Shape: render(r)}
	glyphs[r] = g
	return g
}`,
        choices: [
          { label: 'Singleton' },
          { label: 'Flyweight', correct: true },
          { label: 'None of these' },
          { label: 'Misused' },
        ],
        explanation: 'One shared Glyph per rune is handed to every caller who asks for that rune.',
      },
    ],
  },
  {
    slug: 'proxy',
    teach: [
      {
        label: 'The problem',
        text: 'The real object is costly to build, remote, or not everyone may use it. Callers should not each carry the checks or the waiting logic.',
      },
      {
        label: 'The shape',
        text: "A stand-in has the real object's interface and holds the real one. It decides whether, when and how the call gets through: lazy, cached, checked.",
      },
      {
        label: 'A trap',
        text: 'Proxy and Decorator look alike in code. Ask what the wrapper is for: controlling access means Proxy, adding behaviour means Decorator.',
      },
    ],
    whenToUse:
      'You must control access to an object: build it late, cache its answers, check permissions, or reach it across a network.',
    whenNotTo:
      'The wrapper adds new behaviour rather than gating access, or the object is cheap and open. Then a Decorator or a direct call is clearer.',
    quizzes: [
      {
        format: 'judge',
        prompt: 'This is a Proxy.',
        code: `class TimedRepo implements Repo
{
    public function __construct(
        private Repo $r,
    ) {}

    public function find(int $id): ?Item
    {
        $t = microtime(true);
        $item = $this->r->find($id);
        $this->record(microtime(true) - $t);
        return $item;
    }
}`,
        choices: [{ label: 'True' }, { label: 'False', correct: true }],
        explanation: 'It adds timing to every call and gates nothing: that is a Decorator.',
      },
      {
        format: 'quick_pick',
        prompt: 'Which job suits a Proxy rather than a Decorator?',
        choices: [
          { label: 'Add logging to every call' },
          { label: 'Compress a response body' },
          { label: 'Format output as JSON' },
          { label: 'Build a heavy object on first use', correct: true },
        ],
        explanation: 'Controlling when the real object exists is access control, which is the Proxy job.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `class GuardedFiles implements Files
{
    public function __construct(
        private Files $real,
        private User $user,
    ) {}

    public function read(string $p): string
    {
        abort_unless($this->ok(), 403);
        return $this->real->read($p);
    }
}`,
        choices: [
          { label: 'Decorator' },
          { label: 'None of these' },
          { label: 'Proxy', correct: true },
          { label: 'Misused' },
        ],
        explanation: 'It decides whether the call reaches the real Files at all: a protection Proxy.',
      },
    ],
  },
  {
    slug: 'chain-of-responsibility',
    teach: [
      {
        label: 'The problem',
        text: 'A request could be handled by one of several steps, and the sender should not know which. Hard-wiring the order into one big if-chain makes it rigid.',
      },
      {
        label: 'The shape',
        text: 'Each handler either deals with the request or passes it to the next. Any handler may stop the line. Laravel middleware works exactly this way.',
      },
      {
        label: 'A trap',
        text: 'If every handler always runs and none can stop the request, it is no chain: it is a broadcast. A request nobody handles also needs a plan.',
      },
    ],
    whenToUse:
      'Several steps may act on a request in a set order, any of them might end it, and you want to add or reorder steps freely.',
    whenNotTo:
      'Exactly one handler is always right and known up front, or every step must run no matter what. A direct call or a loop is simpler.',
    quizzes: [
      {
        format: 'judge',
        prompt: 'This is a link in a Chain of Responsibility.',
        code: `class EnsureAdmin
{
    public function handle($r, Closure $n)
    {
        if (! $r->user()?->isAdmin()) {
            abort(403);
        }
        return $n($r);
    }
}`,
        choices: [{ label: 'True', correct: true }, { label: 'False' }],
        explanation: 'It either stops the request or passes it on to $next, like any middleware.',
      },
      {
        format: 'quick_pick',
        prompt: 'What does a handler do with a request it cannot handle?',
        choices: [
          { label: 'Throws it back to the sender' },
          { label: 'Passes it to the next handler', correct: true },
          { label: 'Broadcasts it to all handlers' },
          { label: 'Silently returns null' },
        ],
        explanation: 'Passing on is what turns separate handlers into one chain.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `type Handler interface {
	Handle(r Req) bool
}

func Run(hs []Handler, r Req) {
	for _, h := range hs {
		h.Handle(r)
	}
}`,
        choices: [
          { label: 'Chain of Responsibility' },
          { label: 'Observer' },
          { label: 'None of these' },
          { label: 'Misused', correct: true },
        ],
        explanation: 'The bool result is ignored, so every handler runs and none can stop the request.',
      },
    ],
  },
  {
    slug: 'command',
    teach: [
      {
        label: 'The problem',
        text: 'A method call happens now, in this process, and is gone. You want to run it later, on a worker, retry it on failure, or log what was asked.',
      },
      {
        label: 'The shape',
        text: 'Wrap the request in an object holding its data and one method to run it. Now it can be stored, queued, sent elsewhere, repeated or undone.',
      },
      {
        label: 'A trap',
        text: 'A command should carry what it needs, such as an id, not live connections. Otherwise it cannot survive the trip to a queue.',
      },
    ],
    whenToUse:
      'A request must outlive the call: queued jobs, retries, an audit log, or undo and redo of user actions.',
    whenNotTo:
      'The call is simple and immediate and nobody needs to store, replay or undo it. A plain method call beats a class per action.',
    quizzes: [
      {
        format: 'judge',
        prompt: 'This is a Command.',
        code: `// Save writes the doc right now.
func Save(w io.Writer, doc Doc) error {
	_, err := w.Write(doc.Bytes())
	return err
}
// called once, result used at once
// nothing is stored or queued`,
        choices: [{ label: 'True' }, { label: 'False', correct: true }],
        explanation: 'It is a plain call that runs immediately; no request is held as an object.',
      },
      {
        format: 'quick_pick',
        prompt: 'What does turning a request into an object let you do?',
        choices: [
          { label: 'Run it faster' },
          { label: 'Skip the type checks' },
          { label: 'Queue, retry or undo it later', correct: true },
          { label: 'Share it between classes' },
        ],
        explanation: 'Once a request is data, you can keep it, move it and run it when you choose.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `class SendReceipt implements ShouldQueue
{
    use Queueable;

    public function __construct(
        public Order $order,
    ) {}

    public function handle(Mailer $m): void
    {
        $m->to($this->order->email)->send();
    }
}`,
        choices: [
          { label: 'Command', correct: true },
          { label: 'Strategy' },
          { label: 'None of these' },
          { label: 'Misused' },
        ],
        explanation: 'The request is an object with its data and a handle() that the queue runs later.',
      },
    ],
  },
  {
    slug: 'iterator',
    teach: [
      {
        label: 'The problem',
        text: 'A collection might be an array, a linked list or rows streamed from a database. Callers should loop over it the same way without knowing which.',
      },
      {
        label: 'The shape',
        text: 'The collection offers a way to step through it, and the walker keeps its own position. The loop asks for the next item until none is left.',
      },
      {
        label: 'A trap',
        text: 'A getter that returns the whole inner array is not an iterator. It exposes the storage, which is just what the pattern is meant to hide.',
      },
    ],
    whenToUse:
      'Callers must walk a collection whose layout may change, or one too large to load at once, such as a lazy query cursor.',
    whenNotTo:
      'The collection is a plain array and your language already loops over it. Writing your own iterator class there is noise.',
    quizzes: [
      {
        format: 'judge',
        prompt: 'This is an Iterator.',
        code: `func (l *List) All(yield func(Item) bool) {
	for n := l.head; n != nil; n = n.next {
		if !yield(n.item) {
			return
		}
	}
}
// used: for it := range l.All`,
        choices: [{ label: 'True', correct: true }, { label: 'False' }],
        explanation: 'Callers step through items one by one; the linked-list layout stays hidden.',
      },
      {
        format: 'quick_pick',
        prompt: 'What does an iterator keep so the collection does not?',
        choices: [
          { label: 'The sort order' },
          { label: 'A full copy of the items' },
          { label: 'A lock on the data' },
          { label: 'Where the walk currently is', correct: true },
        ],
        explanation: "The walk's position lives in the iterator, so several walks can run at once.",
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `class Cart
{
    private array $items = [];

    public function items(): array
    {
        return $this->items;
    }
}`,
        choices: [
          { label: 'Iterator' },
          { label: 'Memento' },
          { label: 'None of these', correct: true },
          { label: 'Misused' },
        ],
        explanation: 'A plain getter hands out the raw array; there is no stepping and no hidden storage.',
      },
    ],
  },
]
